import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  Input,
  Select,
  TextArea,
} from '../../components/ui';
import { useLocation } from 'react-router-dom';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Add,
  Search,
  Edit,
  Delete,
  Close,
  LocalOffer,
  Percent,
  CalendarMonth,
  Person,
  Warning,
  CheckCircle,
  Cancel,
  PersonAdd,
  MonetizationOn
} from '../../mui-icons';
import './CouponsPage.css';

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const CouponsPage = () => {
  const { canEdit, canDelete } = useAdminAuth();
  const location = useLocation();
  
  // States
  const [coupons, setCoupons] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [filterInfluencer, setFilterInfluencer] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [formData, setFormData] = useState({
    idinfluencer: '',
    code: '',
    commission_percent: 10,
    commission_months: 12,
    valid_from: '',
    valid_until: '',
    max_uses: '',
    description: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load influencers for dropdown
  useEffect(() => {
    const loadInfluencers = async () => {
      try {
        const response = await adminAPI.getInfluencers({ limit: 100, active_only: 'true' });
        if (response.status === 'success') {
          setInfluencers(response.data.items || []);
        }
      } catch (err) {
        console.error('Erro ao carregar influenciadores:', err);
      }
    };
    loadInfluencers();
  }, []);

  // Load coupons
  const loadCoupons = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(activeOnly && { active_only: true }),
        ...(filterInfluencer && { idinfluencer: filterInfluencer })
      };

      const response = await adminAPI.getCoupons(params);
      
      if (response.status === 'success') {
        setCoupons(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar cupons:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, activeOnly, filterInfluencer]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  // Handle search
  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Open create modal
  const openCreateModal = (preselectedInfluencer = null) => {
    setModalMode('create');
    setFormData({
      idinfluencer: preselectedInfluencer?.idinfluencer || '',
      code: '',
      commission_percent: 10,
      commission_months: 12,
      valid_from: getTodayDate(),
      valid_until: '',
      max_uses: '',
      description: ''
    });
    setFormError('');
    setShowModal(true);
  };
  
  // Check if navigated from InfluencersPage with preselected influencer
  useEffect(() => {
    if (location.state?.openCreateModal && location.state?.preselectedInfluencer) {
      // Wait for influencers to load before opening modal
      const timer = setTimeout(() => {
        openCreateModal(location.state.preselectedInfluencer);
        // Clear the state to prevent reopening on refresh
        window.history.replaceState({}, document.title);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Open edit modal
  const openEditModal = (coupon) => {
    setModalMode('edit');
    setSelectedCoupon(coupon);
    setFormData({
      idinfluencer: coupon.idinfluencer || '',
      code: coupon.code || '',
      commission_percent: coupon.commission_percent || 10,
      commission_months: coupon.commission_months || 12,
      valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      max_uses: coupon.max_uses || '',
      description: coupon.description || ''
    });
    setFormError('');
    setShowModal(true);
  };

  // Open delete modal
  const openDeleteModal = (coupon) => {
    setSelectedCoupon(coupon);
    setShowDeleteModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    // Validation
    if (!formData.code || formData.code.length < 2) {
      setFormError('Código do cupom é obrigatório');
      setFormLoading(false);
      return;
    }

    if (modalMode === 'create' && !formData.idinfluencer) {
      setFormError('Selecione um influenciador');
      setFormLoading(false);
      return;
    }

    try {
      const data = {
        ...formData,
        code: formData.code.toUpperCase(),
        commission_percent: parseFloat(formData.commission_percent) || 10,
        commission_months: parseInt(formData.commission_months) || 12,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };

      if (modalMode === 'create') {
        await adminAPI.createCoupon(data);
      } else {
        // Don't send idinfluencer on edit
        delete data.idinfluencer;
        await adminAPI.updateCoupon(selectedCoupon.idcoupon, data);
      }

      setShowModal(false);
      loadCoupons();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (hardDelete = false) => {
    setDeleteLoading(true);

    try {
      await adminAPI.deleteCoupon(selectedCoupon.idcoupon, hardDelete);
      setShowDeleteModal(false);
      loadCoupons();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Toggle active status
  const toggleActive = async (coupon) => {
    try {
      await adminAPI.updateCoupon(coupon.idcoupon, { active: !coupon.active });
      loadCoupons();
    } catch (err) {
      alert(err.message);
    }
  };

  // Get validity status badge
  const getValidityBadge = (coupon) => {
    const statusMap = {
      'valid': { class: 'status-active', text: 'Válido' },
      'inactive': { class: 'status-inactive', text: 'Inativo' },
      'influencer_inactive': { class: 'status-pending', text: 'Influenciador Inativo' },
      'not_started': { class: 'status-pending', text: 'Não Iniciado' },
      'expired': { class: 'status-inactive', text: 'Expirado' },
      'max_uses_reached': { class: 'status-inactive', text: 'Limite Atingido' }
    };

    const status = statusMap[coupon.validity_status] || { class: 'status-pending', text: 'Desconhecido' };
    return <span className={`status-badge ${status.class}`}>{status.text}</span>;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Render loading skeleton
  if (loading && coupons.length === 0) {
    return (
      <div className="coupons-page">
        <div className="page-header">
          <Skeleton width={200} height={32} />
          <Skeleton width={150} height={40} />
        </div>
        <div className="admin-card pn-card pn-card--default">
          <Skeleton width={300} height={40} style={{ marginBottom: 20 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} style={{ marginBottom: 12 }} />
          ))}
        </div>
      </div>
    );
  }

  // Sub-navigation items para o grupo Influenciadores
  const subNavItems = [
    { path: '/admin/influencers', label: 'Influenciadores', icon: <PersonAdd sx={{ fontSize: 18 }} /> },
    { path: '/admin/coupons', label: 'Cupons', icon: <LocalOffer sx={{ fontSize: 18 }} /> },
    { path: '/admin/commissions', label: 'Comissões', icon: <MonetizationOn sx={{ fontSize: 18 }} /> }
  ];

  return (
    <div className="coupons-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <LocalOffer sx={{ fontSize: 32 }} />
          <div>
            <h1>Cupons</h1>
            <p>{pagination.total} cupom(ns) cadastrado(s)</p>
          </div>
        </div>
        
        {canEdit() && (
          <Button variant="primary" onClick={openCreateModal}>
            <Add />
            Novo Cupom
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por código..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={filterInfluencer}
            onChange={(e) => {
              setFilterInfluencer(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">Todos os influenciadores</option>
            {influencers.map(inf => (
              <option key={inf.idinfluencer} value={inf.idinfluencer}>
                {inf.name}{inf.phone ? ` (${inf.phone})` : ''}
              </option>
            ))}
          </select>

          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
            <span>Apenas ativos</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadCoupons}>
              Tentar novamente
            </Button>
          </div>
        ) : coupons.length === 0 ? (
          <div className="empty-state">
            <LocalOffer sx={{ fontSize: 64 }} className="empty-state-icon" />
            <h3 className="empty-state-title">Nenhum cupom encontrado</h3>
            <p className="empty-state-text">
              {search ? 'Tente uma busca diferente' : 'Comece criando um novo cupom'}
            </p>
            {canEdit() && !search && (
              <Button variant="primary" onClick={openCreateModal}>
                <Add />
                Criar Cupom
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Influenciador</th>
                    <th>Comissão</th>
                    <th>Validade</th>
                    <th>Usos</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.idcoupon}>
                      <td>
                        <span className="coupon-code">{coupon.code}</span>
                      </td>
                      <td>
                        <div className="influencer-cell">
                          <span>{coupon.influencer_name || 'N/A'}</span>
                          {!coupon.influencer_active && (
                            <span className="inactive-tag">Inativo</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="commission-cell">
                          <span className="commission-percent">{coupon.commission_percent}%</span>
                          <span className="commission-months">por {coupon.commission_months} meses</span>
                        </div>
                      </td>
                      <td>
                        <div className="validity-cell">
                          {coupon.valid_from || coupon.valid_until ? (
                            <>
                              <span>{formatDate(coupon.valid_from)}</span>
                              <span className="validity-separator">até</span>
                              <span>{formatDate(coupon.valid_until)}</span>
                            </>
                          ) : (
                            <span className="no-limit">Sem limite</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="uses-cell">
                          <span className="uses-current">{coupon.current_uses || 0}</span>
                          {coupon.max_uses && (
                            <>
                              <span className="uses-separator">/</span>
                              <span className="uses-max">{coupon.max_uses}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>{getValidityBadge(coupon)}</td>
                      <td>
                        <div className="action-buttons">
                          {canEdit() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(coupon)}
                              title="Editar"
                              aria-label="Editar"
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </Button>
                          )}
                          {canEdit() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteModal(coupon)}
                              title="Excluir"
                              aria-label="Excluir"
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>

                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Novo Cupom' : 'Editar Cupom'}
        large
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={formLoading}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" form="coupon-form" loading={formLoading}>
              {modalMode === 'create' ? 'Criar' : 'Salvar'}
            </Button>
          </>
        }
      >
            <form id="coupon-form" onSubmit={handleSubmit} className="modal-form">
              {/* Influenciador (apenas na criação) */}
              {modalMode === 'create' && (
                <div className="form-group">
                  <label className="form-label">Influenciador *</label>
                  <div className="input-container">
                    <Person className="input-icon" />
                    <select
                      className="form-input form-select"
                      value={formData.idinfluencer}
                      onChange={(e) => setFormData({ ...formData, idinfluencer: e.target.value })}
                      disabled={formLoading}
                      required
                    >
                      <option value="">Selecione um influenciador</option>
                      {influencers.map(inf => (
                        <option key={inf.idinfluencer} value={inf.idinfluencer}>
                          {inf.name}{inf.phone ? ` (${inf.phone})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Código */}
              <div className="form-group">
                <label className="form-label">Código do Cupom *</label>
                <div className="input-container">
                  <LocalOffer className="input-icon" />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ex: PROMO20"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    disabled={formLoading}
                    required
                    maxLength={50}
                  />
                </div>
              </div>

              {/* Comissão */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Comissão (%)</label>
                  <div className="input-container">
                    <Percent className="input-icon" />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="10"
                      value={formData.commission_percent}
                      onChange={(e) => setFormData({ ...formData, commission_percent: e.target.value })}
                      disabled={formLoading}
                      min={0.01}
                      max={100}
                      step={0.01}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Meses de Comissão</label>
                  <div className="input-container">
                    <CalendarMonth className="input-icon" />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="12"
                      value={formData.commission_months}
                      onChange={(e) => setFormData({ ...formData, commission_months: e.target.value })}
                      disabled={formLoading}
                      min={1}
                      max={120}
                    />
                  </div>
                </div>
              </div>

              {/* Validade */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Válido a partir de</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Válido até</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    disabled={formLoading}
                  />
                </div>
              </div>

              {/* Limite de usos */}
              <div className="form-group">
                <label className="form-label">Limite de Usos</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Deixe vazio para ilimitado"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  disabled={formLoading}
                  min={1}
                />
              </div>

              {/* Descrição */}
              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  className="form-input form-textarea"
                  placeholder="Descrição do cupom..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={formLoading}
                  rows={3}
                />
              </div>

              {formError && (
                <div className="form-error">
                  <Warning />
                  <span>{formError}</span>
                </div>
              )}

            </form>
      </FormModal>

      <FormModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir Cupom"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => handleDelete(false)} loading={deleteLoading}>
              Desativar
            </Button>
            {canDelete() && (
              <Button
                variant="danger"
                onClick={() => handleDelete(true)}
                loading={deleteLoading}
                style={{ background: '#7B1FA2' }}
              >
                Excluir Permanente
              </Button>
            )}
          </>
        }
      >
        <p>
          Tem certeza que deseja excluir o cupom <strong>{selectedCoupon?.code}</strong>?
        </p>
      </FormModal>
    </div>
  );
};

export default CouponsPage;

